/**
 * @file     ChatShowingCard.tsx
 * @purpose  The in-chat "showing": the full substance of a listing without
 *           leaving the chat. Fetches the audited detail endpoint and renders
 *           photo carousel, description (expandable), a key-details grid
 *           (fees, taxes, parking, pets, heating, listed date), feature chips,
 *           open house, save/map/tour actions. Detail data is display-only:
 *           it never enters the model (ADR-009).
 */
'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAuth } from '@/hooks/useAuth';
import { mlsToCardProps, toDisplay } from '@/features/properties/services/cardAdapters';
import { mlsHeroImageLoader, applyPhotoFallback } from '@/services/mls/mlsImageLoader';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { reltaTrack } from '@/hooks/useReltaTrack';
import type { MlsListingCardData } from '@/types/listing';
import { formatShortDate } from '@/utils/dateFormat';
import { formatLotSize } from '@/utils/formatLotSize';
import { formatCADCents } from '@/utils/finance/mortgage';

const MAX_PHOTOS = 20;
const MAX_FEATURE_CHIPS = 8;

/** The slice of ListingDetail the showing renders (display-only). */
interface ShowingDetail {
  publicRemarks: string | null;
  media: { mediaKey: string; mediaOrder: number | null }[];
  parkingTotal: number | null;
  garageYn: boolean | null;
  associationFee: number | null;
  taxAnnualAmount: number | null;
  taxYear: number | null;
  petsAllowed: string[] | null;
  heating: string[] | null;
  stories: number | null;
  lotSizeSquareFeet: number | null;
  propertySubType: string | null;
  subdivisionName: string | null;
  mlsAreaMinor: string | null;
  extractedFeatures: string[];
  interiorFeatures: string[] | null;
  virtualTourUrl: string | null;
  listedDate: string | null;
  feedProvider: string | null;
  realtorCaUrl: string | null;
  openHouses: { date: string; startTime: string | null }[];
}

const detailCache = new Map<string, ShowingDetail | null>();

async function loadDetail(key: string): Promise<ShowingDetail | null> {
  if (detailCache.has(key)) return detailCache.get(key) ?? null;
  try {
    const res = await fetch(`/api/mls/listings/${encodeURIComponent(key)}`);
    const detail = res.ok ? ((await res.json()) as ShowingDetail) : null;
    detailCache.set(key, detail);
    return detail;
  } catch {
    return null; // card-level facts still render
  }
}

function Arrow({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  return (
    <IconButton
      aria-label={dir}
      size="small"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [dir === 'prev' ? 'left' : 'right']: 8,
        backgroundColor: 'rgba(0,0,0,0.45)', // lint-allow-raw-color: photo overlay scrim
        color: '#fff', // lint-allow-raw-color: photo overlay scrim
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }, // lint-allow-raw-color: photo overlay scrim
        // 44px hit target (iOS HIG) for reliable tapping on the carousel.
        width: 44,
        height: 44,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {dir === 'prev' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 6l6 6-6 6" />}
      </svg>
    </IconButton>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  const { theme } = useAppTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: `1px solid ${theme.semanticColors.divider}` }}>
      <Typography sx={{ fontSize: 12.5, color: theme.semanticColors.textSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: theme.semanticColors.textPrimary, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export function ChatShowingCard({ listing, onOpenListing }: { listing: MlsListingCardData; onOpenListing?: (listing: MlsListingCardData) => void }) {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const { user } = useAuth();
  const display = toDisplay(mlsToCardProps(listing));
  const [detail, setDetail] = useState<ShowingDetail | null>(null);
  const [photos, setPhotos] = useState<Array<{ mediaKey: string; src: string }>>(
    display.primaryImageSrc ? [{ mediaKey: 'primary', src: display.primaryImageSrc }] : [],
  );
  const [idx, setIdx] = useState(0);
  const [saved, setSaved] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    void loadDetail(listing.listingKey).then((d) => {
      if (!alive || !d) return;
      setDetail(d);
      if (d.media.length > 0) {
        setPhotos(
          d.media
            .slice()
            .sort((a, b) => (a.mediaOrder ?? 999) - (b.mediaOrder ?? 999))
            .slice(0, MAX_PHOTOS)
            .map((m) => ({ mediaKey: m.mediaKey, src: `/api/mls/media/${m.mediaKey}` })),
        );
      }
    });
    return () => { alive = false; };
  }, [listing.listingKey]);

  const toggleSave = async () => {
    const next = !saved;
    setSaved(next);
    reltaTrack('ai_chat.match_focus', { listing_key: listing.listingKey });
    try {
      await secureFetch('/api/mls/saved', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: next ? 'save' : 'unsave', listingKey: listing.listingKey }),
      });
    } catch {
      setSaved(!next); // revert on failure
    }
  };

  const headline = [
    display.beds != null ? `${display.beds} ${t('properties.card.bd', { defaultValue: 'bd' })}` : null,
    display.baths != null ? `${display.baths} ${t('properties.card.ba', { defaultValue: 'ba' })}` : null,
    display.sqft != null ? `${Math.round(display.sqft).toLocaleString('en-CA')} ${t('properties.card.sqft', { defaultValue: 'sqft' })}` : null,
    display.pricePerSqft != null ? `$${display.pricePerSqft.toLocaleString('en-CA')}/${t('properties.card.sqft', { defaultValue: 'sqft' })}` : null,
  ].filter(Boolean);

  const neighbourhood = detail?.subdivisionName ?? detail?.mlsAreaMinor ?? null;
  const rows: Array<[string, string]> = [];
  if (detail) {
    const d = detail;
    if (d.propertySubType) rows.push([t('aiChat.detail.type', { defaultValue: 'Type' }), d.propertySubType]);
    if (display.yearBuilt != null) rows.push([t('properties.card.built', { defaultValue: 'Built' }), String(display.yearBuilt)]);
    if (d.associationFee != null && d.associationFee > 0) rows.push([t('aiChat.detail.strata', { defaultValue: 'Strata fee' }), `${formatCADCents(d.associationFee)}/mo`]);
    if (d.taxAnnualAmount != null && d.taxAnnualAmount > 0) rows.push([t('aiChat.detail.taxes', { defaultValue: 'Property taxes' }), `${formatCADCents(d.taxAnnualAmount)}${d.taxYear ? ` (${d.taxYear})` : ''}`]);
    if (d.parkingTotal != null) rows.push([t('aiChat.detail.parking', { defaultValue: 'Parking' }), `${d.parkingTotal}${d.garageYn ? ` · ${t('aiChat.detail.garage', { defaultValue: 'garage' })}` : ''}`]);
    if (d.petsAllowed?.length) rows.push([t('aiChat.detail.pets', { defaultValue: 'Pets' }), d.petsAllowed.join(', ')]);
    if (d.heating?.length) rows.push([t('aiChat.detail.heating', { defaultValue: 'Heating' }), d.heating.join(', ')]);
    if (d.stories != null && d.stories > 0) rows.push([t('aiChat.detail.stories', { defaultValue: 'Levels' }), String(d.stories)]);
    if (d.lotSizeSquareFeet != null && d.lotSizeSquareFeet > 0) rows.push([t('aiChat.detail.lot', { defaultValue: 'Lot size' }), formatLotSize(d.lotSizeSquareFeet, { acres: t('properties.card.acres', { defaultValue: 'acres' }), sqft: t('properties.card.sqft', { defaultValue: 'sqft' }) })!]);
    if (neighbourhood) rows.push([t('aiChat.detail.neighbourhood', { defaultValue: 'Neighbourhood' }), neighbourhood]);
    if (d.listedDate) rows.push([t('aiChat.detail.listed', { defaultValue: 'Listed on' }), formatShortDate(d.listedDate)]);
  }
  const featureChips = Array.from(new Set([...(detail?.extractedFeatures ?? []), ...(detail?.interiorFeatures ?? [])])).slice(0, MAX_FEATURE_CHIPS);
  const remarks = detail?.publicRemarks?.trim() ?? '';

  return (
    <Box
      onClick={onOpenListing ? () => onOpenListing(listing) : undefined}
      role={onOpenListing ? 'button' : undefined}
      aria-label={onOpenListing ? t('aiChat.openThisListing', 'Open this listing') : undefined}
      sx={{
        width: '100%',
        maxWidth: 620,
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        backgroundColor: theme.semanticColors.surfaceCard,
        cursor: onOpenListing ? 'pointer' : 'default',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        ...(onOpenListing && {
          '&:hover': {
            borderColor: theme.palette.brand.primary,
            boxShadow: `0 8px 24px -8px ${alpha(theme.rawSemanticColors.textPrimary, 0.16)}`,
          },
        }),
      }}
    >
      {/* Carousel: a 3-image window (prev/current/next) stays mounted so
          adjacent photos are already decoded, cross-fading on navigation
          instead of lagging while the next one loads. */}
      <Box
        sx={{ position: 'relative', aspectRatio: '16 / 10', backgroundColor: theme.semanticColors.backgroundSecondary, overflow: 'hidden' }}
      >
        {photos.map((photo, i) => {
          const n = photos.length;
          const isWindow = n <= 3
            || i === idx
            || i === (idx + 1) % n
            || i === (idx - 1 + n) % n;
          if (!isWindow) return null;
          return (
            <Box
              key={photo.mediaKey}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: i === idx ? 1 : 0,
                transition: 'opacity 0.35s ease',
                pointerEvents: 'none',
              }}
            >
              <Image
                src={photo.src}
                alt={display.title}
                fill
                sizes="(max-width: 640px) 95vw, 620px"
                loader={mlsHeroImageLoader}
                onError={(e) => applyPhotoFallback(e.currentTarget as HTMLImageElement)}
                style={{ objectFit: 'cover' }}
              />
            </Box>
          );
        })}
        {photos.length > 1 && (
          <>
            <Arrow dir="prev" onClick={() => setIdx((i) => (i - 1 + photos.length) % photos.length)} />
            <Arrow dir="next" onClick={() => setIdx((i) => (i + 1) % photos.length)} />
            <Box
              sx={{
                position: 'absolute', bottom: 8, right: 10, px: 1, py: 0.25, borderRadius: 1,
                fontSize: 11.5, fontWeight: 600,
                backgroundColor: 'rgba(0,0,0,0.55)', // lint-allow-raw-color: photo overlay scrim
                color: '#fff', // lint-allow-raw-color: photo overlay scrim
              }}
            >
              {idx + 1} / {photos.length}
            </Box>
          </>
        )}
        {display.freshnessLabel && (
          <Box
            sx={{
              position: 'absolute', top: 10, left: 10, px: 1, py: 0.25, borderRadius: 1,
              fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
              backgroundColor: theme.palette.brand.primary,
              color: theme.semanticColors.textOnPrimary,
            }}
          >
            {display.freshnessLabel}
          </Box>
        )}
        {user && (
          <IconButton
            aria-label={saved ? t('aiChat.unsave', 'Remove from saved') : t('aiChat.save', 'Save this home')}
            onClick={(e) => { e.stopPropagation(); void toggleSave(); }}
            sx={{
              position: 'absolute', top: 8, right: 8,
              backgroundColor: 'rgba(0,0,0,0.45)', // lint-allow-raw-color: photo overlay scrim
              color: saved ? theme.semanticColors.favoriteActive : '#fff', // lint-allow-raw-color: photo overlay scrim
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }, // lint-allow-raw-color: photo overlay scrim
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </IconButton>
        )}
      </Box>

      {/* Substance */}
      <Box sx={{ px: 2, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: theme.semanticColors.textPrimary }}>
            {display.price != null ? `$${display.price.toLocaleString('en-CA')}` : ''}
          </Typography>
          {display.mlsId && (
            <Typography sx={{ fontSize: 11.5, color: theme.semanticColors.textMuted }}>MLS® {display.mlsId}</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: theme.semanticColors.textPrimary }}>
          {display.title}{display.subtitle ? `, ${display.subtitle}` : ''}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: theme.semanticColors.textPrimary }}>
          {headline.join(' · ')}
        </Typography>
        {display.nextOpenHouseLabel && (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.brand.primary }}>
            {display.nextOpenHouseLabel}
          </Typography>
        )}

        {/* Description, expandable in place */}
        {remarks && (
          <Box sx={{ mt: 0.5 }}>
            <Typography
              sx={{
                fontSize: 13.5,
                lineHeight: 1.6,
                color: theme.semanticColors.textSecondary,
                ...(remarksOpen ? {} : {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }),
              }}
            >
              {remarks}
            </Typography>
            {remarks.length > 180 && (
              <Typography
                onClick={(e) => { e.stopPropagation(); setRemarksOpen((v) => !v); }}
                sx={{ fontSize: 12.5, fontWeight: 600, color: theme.palette.brand.primary, cursor: 'pointer', mt: 0.25 }}
              >
                {remarksOpen ? t('aiChat.readLess', 'Show less') : t('aiChat.readMore', 'Read the full description')}
              </Typography>
            )}
          </Box>
        )}

        {/* Key details grid */}
        {rows.length > 0 && (
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
            {rows.map(([label, value]) => (
              <DetailRow key={label} label={label} value={value} />
            ))}
          </Box>
        )}

        {/* Feature chips */}
        {featureChips.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {featureChips.map((f) => (
              <Chip
                key={f}
                label={f}
                size="small"
                sx={{
                  borderRadius: 1, fontSize: 11, height: 22,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.semanticColors.borderSubtle}`,
                  color: theme.semanticColors.textSecondary,
                }}
              />
            ))}
          </Box>
        )}

        {display.attribution && (
          <Typography sx={{ fontSize: 11, fontStyle: 'italic', color: theme.semanticColors.textMuted, mt: 1 }}>
            {display.attribution}
          </Typography>
        )}
        {detail?.feedProvider === 'ddf' && detail.realtorCaUrl && (
          <Typography sx={{ fontSize: 11, color: theme.semanticColors.textMuted }}>
            <a href={detail.realtorCaUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }} onClick={(e) => e.stopPropagation()}>
              Powered by REALTOR.ca
            </a>
          </Typography>
        )}
      </Box>
    </Box>
  );
}
